import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import type { MenuItem } from '@/shared/types/common';
import { CHAT_UNREAD_BADGE_CLASS } from '@/features/Chat';
import { Svg } from '@/shared/ui/Svg';
import ChevronDownIcon from '@/shared/assets/icons/chevron-down.svg';
import styles from '../../ui/Sidebar/Sidebar.module.scss';

export const NestedMenuItem = ({
  item,
  pathname,
  childBadgeCounts,
  childBadgeHrefs,
}: {
  item: MenuItem;
  pathname: string;
  childBadgeCounts?: Record<string, number | undefined>;
  /** Куда ведёт клик по счётчику, если не туда же, куда пункт (например, сразу на фильтр). */
  childBadgeHrefs?: Record<string, string | undefined>;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;

  const isParentActive = item.href && pathname === item.href;

  const activeChild = hasChildren
    ? item.children?.find((child) => {
        if (!child.href) return false;
        const [childPath] = child.href.split('?');
        return pathname === childPath || pathname.startsWith(`${childPath}/`);
      })
    : null;

  const isChildActive = !!activeChild;
  const [isOpen, setIsOpen] = useState(isChildActive || false);

  React.useEffect(() => {
    if (isChildActive) {
      setIsOpen(true);
    }
  }, [isChildActive, location]);

  const shouldHighlightParent = isParentActive && !isChildActive;

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        data-menu-active={shouldHighlightParent || isChildActive ? 'true' : undefined}
        className={cn(styles.listBoxItemBase, {
          ['bg-red']: shouldHighlightParent || isChildActive,
        })}
      >
        <div className="flex items-center gap-3 w-full">
          {item.icon && (
            <Svg
              Icon={item.icon}
              className={cn({ ['!text-white']: shouldHighlightParent || isChildActive })}
              width={24}
              height={24}
            />
          )}
          <span
            className={cn(styles.listBoxTitle, {
              ['!text-white']: shouldHighlightParent || isChildActive,
            })}
          >
            {item.label}
          </span>
          <div
            className={cn('ml-auto transition-transform', {
              'rotate-180': isOpen,
            })}
          >
            <Svg
              Icon={ChevronDownIcon}
              className={cn('text-black', { ['text-white']: isChildActive })}
            />
          </div>
        </div>
      </button>
      <div
        className={cn('overflow-hidden transition-all duration-300 ease-in-out', {
          'max-h-0': !isOpen,
          'max-h-[500px]': isOpen,
        })}
      >
        <div className="flex flex-col gap-2 mt-2 pl-8">
          {item.children?.map((child) => {
            if (!child.href) return null;
            const [childPath] = child.href.split('?');
            const isActiveChild = pathname === childPath || pathname.startsWith(`${childPath}/`);
            const badgeCount = childBadgeCounts?.[child.href];
            const badgeHref = childBadgeHrefs?.[child.href];
            return (
              <Link
                key={child.id}
                to={child.href}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(child.href!);
                }}
                data-menu-active={isActiveChild ? 'true' : undefined}
                className={cn(styles.listBoxItemBase, 'text-sm', {
                  ['bg-[#FFFAEE]']: isActiveChild,
                })}
              >
                <div className="flex items-center gap-2 w-full min-w-0">
                  <span
                    className={cn(styles.listBoxTitle, 'text-sm', {
                      ['!font-bold']: isActiveChild,
                    })}
                  >
                    {child.label}
                  </span>
                  {badgeCount != null && badgeCount > 0 ? (
                    <span
                      className={cn(CHAT_UNREAD_BADGE_CLASS, 'ml-auto')}
                      {...(badgeHref
                        ? {
                            role: 'link',
                            tabIndex: 0,
                            onClick: (event: React.MouseEvent) => {
                              event.preventDefault();
                              event.stopPropagation();
                              navigate(badgeHref);
                            },
                            onKeyDown: (event: React.KeyboardEvent) => {
                              if (event.key !== 'Enter' && event.key !== ' ') return;
                              event.preventDefault();
                              event.stopPropagation();
                              navigate(badgeHref);
                            },
                          }
                        : {})}
                    >
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  ) : null}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};
